/**
 * MTGA text format parser
 *
 * Format:
 * 4 Lightning Bolt (M10) 146
 * 2 Counterspell
 *
 * Also supports:
 * // Comments (ignored)
 * # Comments (ignored)
 * Blank lines (ignored)
 *
 * Delegates line-level parsing to the shared `parseCardLine()`.
 */
import { parseCardLine } from '@/lib/cardLineParser';
import type { ImportFieldSummary, MatchingStrategy } from '@/lib/universalCSVParser';
import type { ImportCard } from '@/types/schemas';

// ============================================================================
// Types
// ============================================================================

export interface MtgaParseResult {
  cards: ImportCard[];
  fieldSummary: ImportFieldSummary;
}

/**
 * Parse MTGA text format
 */
export function parseMtga(text: string): ImportCard[] {
  return parseMtgaWithSummary(text).cards;
}

/**
 * Parse MTGA text format with field summary
 */
export function parseMtgaWithSummary(text: string): MtgaParseResult {
  const lines = text.trim().split(/\r?\n/);
  const results: ImportCard[] = [];

  for (const line of lines) {
    const trimmed = line.trim();

    // Skip empty lines and comments
    if (!trimmed || trimmed.startsWith('//') || trimmed.startsWith('#')) {
      continue;
    }

    const parsed = parseCardLine(trimmed);
    if (parsed) {
      results.push({
        name: parsed.name,
        setCode: parsed.setCode,
        collectorNumber: parsed.collectorNumber,
        quantity: parsed.quantity,
        finish: parsed.finish,
        condition: 'NM',
        language: 'en',
        purchasePrice: null,
        purchaseCurrency: null,
        purchaseDate: null,
        isAltered: false,
        isPlaytest: false,
        folderName: null,
        sourceId: null,
        scryfallId: null,
      });
    }
  }

  // Determine what fields were captured
  const hasSetCode = results.some((c) => c.setCode);
  const hasCollectorNumber = results.some((c) => c.collectorNumber);

  // Build field summary for MTGA format
  const fieldsUsed: string[] = ['Quantity', 'Name'];
  if (hasSetCode) fieldsUsed.push('Set Code');
  if (hasCollectorNumber) fieldsUsed.push('Collector Number');

  // MTGA with set+collector can use fingerprint matching
  const matchingStrategy: MatchingStrategy = hasSetCode && hasCollectorNumber ? 'fingerprint' : 'none';

  const fieldSummary: ImportFieldSummary = {
    sourceName: 'MTG Arena',
    fieldsUsed,
    fieldsIgnored: [],
    matchingStrategy,
  };

  return { cards: results, fieldSummary };
}
